import React, { useState, useEffect, useRef } from 'react';

// Base interface for autocomplete items
export interface AutocompleteItem {
  id: number;
  name: string;
  [key: string]: any;
}

// Mode types
type AutocompleteMode = 'multi' | 'single' | 'string';

// Generic props for all modes
interface BaseAutocompleteProps {
  mode: AutocompleteMode;
  placeholder?: string;
  minQueryLength?: number;
  debounceMs?: number;
  maxSuggestions?: number;
  allowCreate?: boolean;
  allowClear?: boolean;
  searchingText?: string;
  createButtonText?: (query: string) => string;
  noItemsFoundText?: (query: string) => string;
  additionalCreateData?: { [key: string]: any };
  className?: string;
}

// Props for multi-select mode
interface MultiSelectProps<T extends AutocompleteItem> extends BaseAutocompleteProps {
  mode: 'multi';
  selectedItems: T[];
  onItemSelect: (item: T) => void;
  onItemRemove: (itemId: number) => void;
  searchFunction: (query: string, limit?: number) => Promise<T[]>;
  createFunction: (data: { name: string; [key: string]: any }) => Promise<T>;
  itemDisplayName?: (item: T) => string;
  itemClassName?: string;
  selectedItemClassName?: string;
  createItemClassName?: string;
}

// Props for single-select mode
interface SingleSelectProps<T extends AutocompleteItem> extends BaseAutocompleteProps {
  mode: 'single';
  selectedItem: T | null;
  onItemSelect: (item: T | null) => void;
  searchFunction: (query: string, limit?: number) => Promise<T[]>;
  createFunction: (data: { name: string; [key: string]: any }) => Promise<T>;
  itemDisplayName?: (item: T) => string;
  itemDisplayExtra?: (item: T) => React.ReactNode;
}

// Props for string mode
interface StringProps extends BaseAutocompleteProps {
  mode: 'string';
  value: string;
  onChange: (value: string) => void;
  searchFunction: (query: string, limit?: number) => Promise<string[]>;
  createFunction?: (data: { name: string }) => Promise<string>;
}

// Union type for all props
type UnifiedAutocompleteProps<T extends AutocompleteItem> = 
  | MultiSelectProps<T>
  | SingleSelectProps<T>
  | StringProps;

const UnifiedAutocomplete = <T extends AutocompleteItem>({
  mode,
  placeholder,
  minQueryLength = 1,
  debounceMs = 500,
  maxSuggestions = 10,
  allowCreate = true,
  allowClear = true,
  searchingText = "Searching...",
  createButtonText = (query: string) => `+ Add "${query}"`,
  noItemsFoundText = (query: string) => `No items found. Press Enter to add "${query}"`,
  additionalCreateData = {},
  className = "",
  ...props
}: UnifiedAutocompleteProps<T>) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<T[] | string[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get specific values from props to avoid broad dependency
  const singleSelectedItem = mode === 'single' ? (props as SingleSelectProps<T>).selectedItem : null;
  const stringValue = mode === 'string' ? (props as StringProps).value : '';
  const singleItemDisplayName = mode === 'single' ? (props as SingleSelectProps<T>).itemDisplayName : undefined;
  
  // Initialize query based on mode
  useEffect(() => {
    if (mode === 'single' && !isUserTyping && singleSelectedItem && !showSuggestions) {
      const displayName = singleItemDisplayName || ((item: T) => item.name);
      setQuery(displayName(singleSelectedItem));
    } else if (mode === 'string' && !isUserTyping) {
      setQuery(stringValue || '');
    }
  }, [mode, isUserTyping, singleSelectedItem, stringValue, singleItemDisplayName, showSuggestions]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search effect
  useEffect(() => {
    const searchWithDelay = async () => {
      // Clear suggestions if query is too short
      if (query.trim().length < minQueryLength) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      // Only perform new searches when user is actively typing
      if (!isUserTyping) {
        return;
      }

      
      // Show loading state after a short delay to prevent flicker for fast responses
      loadingTimeoutRef.current = setTimeout(() => {
        setShowLoading(true);
      }, 150);
      
      try {
        const results = await props.searchFunction(query.trim(), maxSuggestions);
        
        if (mode === 'multi') {
          const multiProps = props as MultiSelectProps<T>;
          const filteredResults = (results as T[]).filter(
            item => !multiProps.selectedItems.some(selected => selected.id === item.id)
          );
          setSuggestions(filteredResults);
        } else {
          setSuggestions(results);
        }
        
        setShowSuggestions(true);
        setSelectedIndex(-1);
      } catch (error) {
        
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setShowLoading(false);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
      }
    };

    const debounceTimer = setTimeout(searchWithDelay, debounceMs);
    return () => clearTimeout(debounceTimer);
  }, [query, isUserTyping, minQueryLength, maxSuggestions, debounceMs, mode, props]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    setIsUserTyping(true);
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set timeout to reset typing state after user stops typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsUserTyping(false);
    }, debounceMs + 500);
    
    if (mode === 'string') {
      const stringProps = props as StringProps;
      stringProps.onChange(newValue);
    } else if (mode === 'single' && !newValue) {
      const singleProps = props as SingleSelectProps<T>;
      singleProps.onItemSelect(null);
    }
  };

  const handleItemSelect = async (item: T | string) => {
    if (mode === 'multi') {
      const multiProps = props as MultiSelectProps<T>;
      multiProps.onItemSelect(item as T);
      setQuery('');
      setSuggestions([]);
      setShowSuggestions(false);
      inputRef.current?.focus();
    } else if (mode === 'single') {
      const singleProps = props as SingleSelectProps<T>;
      const displayName = singleProps.itemDisplayName || ((item: T) => item.name);
      singleProps.onItemSelect(item as T);
      setQuery(displayName(item as T));
      setIsUserTyping(false);
      setSuggestions([]);
      setShowSuggestions(false);
      inputRef.current?.blur();
    } else if (mode === 'string') {
      const stringProps = props as StringProps;
      const value = item as string;
      setQuery(value);
      stringProps.onChange(value);
      setIsUserTyping(false);
      setSuggestions([]);
      setShowSuggestions(false);
      inputRef.current?.blur();
    }
  };

  const handleCreateItem = async () => {
    if (!query.trim() || !allowCreate) return;

    try {
      if (mode === 'multi') {
        const multiProps = props as MultiSelectProps<T>;
        const createData = { name: query.trim(), ...additionalCreateData };
        const newItem = await multiProps.createFunction(createData);
        multiProps.onItemSelect(newItem);
        setQuery('');
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
      } else if (mode === 'single') {
        const singleProps = props as SingleSelectProps<T>;
        const createData = { name: query.trim(), ...additionalCreateData };
        const newItem = await singleProps.createFunction(createData);
        const displayName = singleProps.itemDisplayName || ((item: T) => item.name);
        singleProps.onItemSelect(newItem);
        setQuery(displayName(newItem));
        setIsUserTyping(false);
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.blur();
      } else if (mode === 'string') {
        const stringProps = props as StringProps;
        const trimmedValue = query.trim();
        
        if (stringProps.createFunction && suggestions.length === 0) {
          try {
            const createdValue = await stringProps.createFunction({ name: trimmedValue });
            setQuery(createdValue);
            stringProps.onChange(createdValue);
          } catch (error) {
            
            setQuery(trimmedValue);
            stringProps.onChange(trimmedValue);
          }
        } else {
          setQuery(trimmedValue);
          stringProps.onChange(trimmedValue);
        }
        
        setIsUserTyping(false);
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.blur();
      }
    } catch (error) {
      
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions) return;

    const suggestionCount = suggestions.length;
    const maxIndex = suggestionCount + (allowCreate && query.trim() ? 0 : -1);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < maxIndex ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > -1 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex === -1) {
          if (allowCreate) handleCreateItem();
        } else if (selectedIndex === suggestionCount && allowCreate && query.trim()) {
          handleCreateItem();
        } else if (suggestions[selectedIndex]) {
          handleItemSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      if (!suggestionsRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    }, 200);
  };

  const handleInputFocus = () => {
    // Show existing suggestions on focus if available, or trigger search if query exists
    if (query.trim().length >= minQueryLength) {
      if (suggestions.length > 0) {
        setShowSuggestions(true);
      } else {
        // If no suggestions exist but query is long enough, trigger search
        setIsUserTyping(true);
      }
    }
  };

  const handleClear = () => {
    setQuery('');
    if (mode === 'single') {
      const singleProps = props as SingleSelectProps<T>;
      singleProps.onItemSelect(null);
    } else if (mode === 'string') {
      const stringProps = props as StringProps;
      stringProps.onChange('');
    }
    inputRef.current?.focus();
  };

  const getItemDisplayName = (item: T | string): string => {
    if (typeof item === 'string') return item;
    if (mode === 'multi') {
      const multiProps = props as MultiSelectProps<T>;
      return multiProps.itemDisplayName ? multiProps.itemDisplayName(item) : item.name;
    } else if (mode === 'single') {
      const singleProps = props as SingleSelectProps<T>;
      return singleProps.itemDisplayName ? singleProps.itemDisplayName(item) : item.name;
    }
    return item.name;
  };

  const getItemDisplayExtra = (item: T): React.ReactNode => {
    if (mode === 'single') {
      const singleProps = props as SingleSelectProps<T>;
      return singleProps.itemDisplayExtra ? singleProps.itemDisplayExtra(item) : null;
    }
    return null;
  };

  const renderSuggestions = () => {
    if (showLoading) {
      return (
        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
          {searchingText}
        </div>
      );
    }

    return (
      <>
        {suggestions.map((item, index) => (
          <button
            key={typeof item === 'string' ? index : (item as T).id}
            type="button"
            onClick={() => handleItemSelect(item)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
              selectedIndex === index ? 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200' : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {getItemDisplayName(item)}
            {typeof item !== 'string' && getItemDisplayExtra(item as T)}
          </button>
        ))}
        
        {allowCreate && query.trim() && (
          <div className="border-t border-gray-200 dark:border-gray-600">
            <button
              type="button"
              onClick={handleCreateItem}
              className={`w-full text-left px-3 py-2 text-sm text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900 ${
                selectedIndex === suggestions.length ? 'bg-green-50 dark:bg-green-900' : ''
              }`}
            >
              {createButtonText(query.trim())}
            </button>
          </div>
        )}
        
        {suggestions.length === 0 && query.trim() && !showLoading && allowCreate && (
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
            {noItemsFoundText(query.trim())}
          </div>
        )}
      </>
    );
  };

  const showClearButton = allowClear && (
    (mode === 'single' && (props as SingleSelectProps<T>).selectedItem) ||
    (mode === 'string' && (props as StringProps).value)
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={`w-full px-3 py-2 ${showClearButton ? 'pr-8' : ''} border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500`}
        />
        {showClearButton && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ×
          </button>
        )}
        
        {showSuggestions && (
          <div 
            ref={suggestionsRef}
            className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {renderSuggestions()}
          </div>
        )}
      </div>

      {/* Selected items display for multi-select mode */}
      {mode === 'multi' && (props as MultiSelectProps<T>).selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(props as MultiSelectProps<T>).selectedItems.map((item) => {
            const multiProps = props as MultiSelectProps<T>;
            const displayName = multiProps.itemDisplayName ? multiProps.itemDisplayName(item) : item.name;
            return (
              <span
                key={item.id}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                  multiProps.selectedItemClassName || 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                }`}
              >
                {displayName}
                <button
                  type="button"
                  onClick={() => multiProps.onItemRemove(item.id)}
                  className="ml-1 hover:opacity-80"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UnifiedAutocomplete;